import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Search, Ruler, FileText, Coins, ArrowRight, CheckCircle2,
    ShieldCheck, CloudHail, Calculator, Sparkles, FileSignature,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Logo } from '@/components/Logo';
import api from '@/lib/api';

export default function LandingPage() {
    const [plans, setPlans] = useState({});
    const [annual, setAnnual] = useState(false);

    useEffect(() => {
        api.get('/billing/plans').then((r) => setPlans(r.data.plans || {})).catch(() => {});
    }, []);

    const tiers = [
        { key: 'solo', name: 'Solo', desc: 'Independent roofers' },
        { key: 'crew', name: 'Crew', desc: '3 seats included · unlimited lookups', highlight: true },
        { key: 'business', name: 'Business', desc: '6 seats · unlimited extra seats · unlimited lookups' },
    ];

    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* Nav */}
            <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <Logo />
                    <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
                        <a href="#features" className="hover:text-slate-900">Features</a>
                        <a href="#pricing" className="hover:text-slate-900">Pricing</a>
                        <a href="#how" className="hover:text-slate-900">How it works</a>
                    </nav>
                    <div className="flex items-center gap-2">
                        <Button asChild variant="ghost" data-testid="nav-login-button"><Link to="/login">Sign in</Link></Button>
                        <Button asChild className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="nav-signup-button">
                            <Link to="/signup">Start free</Link>
                        </Button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden qf-hero-bg qf-noise">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7 animate-fade-in-up">
                        <Badge variant="outline" className="bg-white/80 border-amber-200 text-amber-700 font-medium">Built for roofers</Badge>
                        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display tracking-tight text-slate-900 leading-[1.05]">
                            Measure roofs, build quotes, and close jobs <span className="text-amber-500">faster</span>.
                        </h1>
                        <p className="mt-5 text-lg text-slate-600 max-w-xl">
                            QuotesFlow combines AI roof measurement, public property data, and a fully-loaded quote builder so independent roofers and crews can hand a professional, e-signable quote to a homeowner in minutes.
                        </p>
                        <div className="mt-7 flex flex-col sm:flex-row gap-3">
                            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-5" data-testid="hero-cta-signup">
                                <Link to="/signup">Start 7-day free trial <ArrowRight className="h-4 w-4 ml-1" /></Link>
                            </Button>
                            <Button asChild size="lg" variant="outline" className="h-12 px-5 border-slate-300" data-testid="hero-cta-pricing">
                                <a href="#pricing">View pricing</a>
                            </Button>
                        </div>
                        <p className="mt-3 text-xs text-slate-500 inline-flex items-center gap-1.5">
                            <ShieldCheck className="h-3 w-3 text-emerald-600" />
                            Card required upfront. Full access for 7 days. Cancel anytime before day 8 — you won&apos;t be charged.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-4 text-sm text-slate-600">
                            <span className="inline-flex items-center gap-2"><Ruler className="h-4 w-4 text-slate-700" /> AI roof measurement</span>
                            <span className="inline-flex items-center gap-2"><CloudHail className="h-4 w-4 text-sky-600" /> 24-mo storm history</span>
                            <span className="inline-flex items-center gap-2"><FileSignature className="h-4 w-4 text-amber-600" /> PDF + e-sign included</span>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <Card className="absolute -top-8 -left-2 rotate-[-3deg] shadow-lg max-w-[260px] hidden sm:block">
                                <CardContent className="pt-5 pb-4 px-4">
                                    <div className="text-xs text-slate-500">Lead unlocked</div>
                                    <div className="font-display font-semibold mt-1">1247 Sycamore Ln</div>
                                    <div className="text-sm text-slate-600">Orlando, FL 32810 · Built 1992</div>
                                    <Badge className="mt-3 bg-emerald-50 text-emerald-700 border border-emerald-200 font-normal">New lead</Badge>
                                </CardContent>
                            </Card>
                            <Card className="shadow-2xl">
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="font-display">Quote #Q-2026-A41B</CardTitle>
                                        <Badge className="bg-amber-50 text-amber-800 border border-amber-200 font-normal">Sent</Badge>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex justify-between text-sm"><span className="text-slate-500">Materials</span><span className="tabular-nums font-medium">$6,800.00</span></div>
                                    <div className="flex justify-between text-sm"><span className="text-slate-500">Labor</span><span className="tabular-nums font-medium">$4,200.00</span></div>
                                    <div className="flex justify-between text-sm"><span className="text-slate-500">Tear-off &amp; Disposal</span><span className="tabular-nums font-medium">$650.00</span></div>
                                    <div className="flex justify-between text-sm"><span className="text-slate-500">City Permit</span><span className="tabular-nums font-medium">$180.00</span></div>
                                    <div className="flex justify-between text-sm"><span className="text-slate-500">Overhead &amp; profit</span><span className="tabular-nums font-medium">$828.10</span></div>
                                    <div className="border-t pt-3 flex justify-between font-semibold"><span>Total</span><span className="tabular-nums text-slate-900">$12,658.10</span></div>
                                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-normal"><CheckCircle2 className="h-3 w-3 mr-1" /> Homeowner signed</Badge>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-16 sm:py-20 bg-white border-t border-slate-200">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-display tracking-tight text-slate-900">Every step. Faster.</h2>
                        <p className="mt-3 text-slate-600">From the first measurement to a signed contract — without juggling six tools.</p>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {[
                            { icon: Search, title: 'Lead Finder', desc: 'Search any US ZIP using public property records — or use the address autocomplete to instantly look up one specific house. Filter by year built, property value, and roof age.' },
                            { icon: Ruler, title: 'AI Roof Measurement', desc: 'Drop a pin on the rooftop and our AI Vision returns square footage, pitch, material, and complexity in seconds — with a green / amber / red confidence badge so you know when to trust it.' },
                            { icon: FileText, title: 'Single-page quote builder', desc: 'Every section editable inline with a live PDF preview pane. Auto-fills costs from your own price list. Download, hand-deliver, text, or send via e-sign link.' },
                            { icon: CloudHail, title: '24-month storm history', desc: 'Every property card shows confirmed hail, wind, tornado, and severe-weather events within 10 miles — with hail size and wind speed.' },
                            { icon: Calculator, title: 'Pricing you can defend', desc: 'Itemized "Overhead & profit" line (the insurance-adjuster standard), waste-factor math shown to the homeowner, and a low–high quote range.' },
                            { icon: Coins, title: 'Credits that fit your size', desc: 'Solo gets 100 lookups/month + optional top-up packs that never expire. Crew & Business get UNLIMITED lookups, unlimited AI estimates, and unlimited quotes.' },
                        ].map((f) => (
                            <Card key={f.title} className="hover:border-slate-300">
                                <CardContent className="pt-6">
                                    <div className="h-10 w-10 rounded-lg bg-slate-900 text-amber-500 flex items-center justify-center mb-4">
                                        <f.icon className="h-5 w-5" strokeWidth={1.75} />
                                    </div>
                                    <h3 className="font-display text-lg text-slate-900">{f.title}</h3>
                                    <p className="mt-1.5 text-sm text-slate-600">{f.desc}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* How */}
            <section id="how" className="py-16 sm:py-20 bg-slate-50 border-t border-slate-200">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-display tracking-tight text-slate-900">How it works</h2>
                    </div>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-5">
                        {[
                            { n: '1', title: 'Start your free trial', desc: 'Pick Solo, Crew, or Business (monthly or annual). Enter your card at Stripe Checkout — you won\'t be charged for 7 days.' },
                            { n: '2', title: 'Find a property', desc: 'Search a US ZIP or paste in any address (we autocomplete it). Each result shows storm activity in the last 24 months.' },
                            { n: '3', title: 'Unlock + measure', desc: 'Solo spends 1 credit per lead; Crew & Business are unlimited. Drop a pin and our AI sizes the roof.' },
                            { n: '4', title: 'Build the quote', desc: 'One scrollable page with a live PDF preview. Auto-filled costs from your own price list. Download, hand-deliver, or e-sign.' },
                        ].map((s) => (
                            <Card key={s.n} className="text-left">
                                <CardContent className="pt-6">
                                    <div className="text-amber-600 font-display text-3xl tabular-nums">{s.n}</div>
                                    <h3 className="mt-2 font-display text-lg text-slate-900">{s.title}</h3>
                                    <p className="text-sm text-slate-600 mt-1.5">{s.desc}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="py-16 sm:py-20 bg-white border-t border-slate-200">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-display tracking-tight text-slate-900">Simple, roofer-friendly pricing</h2>
                        <p className="mt-3 text-slate-600">
                            <b>Every plan starts with a 7-day free trial</b> — card required upfront, no charge for 7 days, cancel anytime. All plans include unlimited quotes &amp; PDF + e-sign. <b>Crew</b> &amp; <b>Business</b> include <b>unlimited lead lookups and AI estimates</b>; <b>Solo</b> is metered with optional top-up packs that never expire.
                        </p>
                    </div>
                    <div className="mt-8 flex items-center justify-center gap-3">
                        <span className={!annual ? 'font-semibold text-slate-900' : 'text-slate-500'}>Monthly</span>
                        <button
                            type="button"
                            onClick={() => setAnnual((v) => !v)}
                            data-testid="pricing-toggle-annual"
                            className={`relative inline-flex h-6 w-11 rounded-full ${annual ? 'bg-amber-500' : 'bg-slate-300'}`}
                        >
                            <span className={`block h-5 w-5 rounded-full bg-white shadow translate-y-0.5 ${annual ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                        <span className={annual ? 'font-semibold text-slate-900' : 'text-slate-500'}>Annual <span className="text-emerald-600 text-xs font-medium">(save up to $1,200/yr)</span></span>
                    </div>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {tiers.map((tier) => {
                            const planKey = `${tier.key}_${annual ? 'annual' : 'monthly'}`;
                            const plan = plans[planKey];
                            if (!plan) return null;
                            const monthly = annual ? (plan.amount / 100 / 12) : (plan.amount / 100);
                            return (
                                <Card key={tier.key} className={`relative ${tier.highlight ? 'border-amber-500 shadow-xl' : 'border-slate-200'}`}>
                                    {tier.highlight && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                            <Badge className="bg-amber-500 text-slate-950 font-semibold border-0">Most popular</Badge>
                                        </div>
                                    )}
                                    <CardHeader className="pb-2">
                                        <CardTitle className="font-display text-2xl text-slate-900">{tier.name}</CardTitle>
                                        <div className="text-sm text-slate-500">{tier.desc}</div>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="mt-2 flex items-baseline gap-1">
                                            <span className="text-4xl font-display font-semibold text-slate-900 tabular-nums">${monthly.toFixed(0)}</span>
                                            <span className="text-slate-500">/mo</span>
                                            {annual && <span className="text-xs text-slate-500">billed annually</span>}
                                        </div>
                                        <ul className="mt-5 space-y-2 text-sm text-slate-700">
                                            <li className="flex items-start gap-2">
                                                <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
                                                {plan.unlimited_credits
                                                    ? <span><b>Unlimited</b> lead lookups &amp; AI estimates</span>
                                                    : <span>{plan.credits} lead lookups / month <span className="text-slate-500">· top-ups never expire</span></span>}
                                            </li>
                                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> {plan.seats} seat{plan.seats > 1 ? 's' : ''} included</li>
                                            {plan.max_extra_seats > 0 && (
                                                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> Add extra seats {plan.max_extra_seats >= 100 ? 'unlimited' : `up to ${plan.max_extra_seats}`} at $29/mo each</li>
                                            )}
                                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> Unlimited quotes, PDF + e-sign</li>
                                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> 24-month storm history per property</li>
                                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> Live PDF preview &amp; auto-filled costs</li>
                                        </ul>
                                        <Button
                                            asChild
                                            className={`w-full mt-6 ${tier.highlight ? 'bg-amber-500 hover:bg-amber-400 text-slate-950' : 'bg-slate-900 hover:bg-slate-800 text-white'} font-semibold`}
                                            data-testid={`pricing-cta-${tier.key}-${annual ? 'annual' : 'monthly'}`}
                                        >
                                            <Link to="/signup">Start with {tier.name}</Link>
                                        </Button>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 bg-slate-900 text-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
                    <h2 className="text-3xl sm:text-4xl font-display tracking-tight">Stop chasing leads. Start signing roofs.</h2>
                    <p className="mt-3 text-slate-300">Get started in under 60 seconds. Cancel anytime.</p>
                    <Button asChild size="lg" className="mt-6 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-6" data-testid="cta-bottom-signup">
                        <Link to="/signup">Create my account <Sparkles className="h-4 w-4 ml-1" /></Link>
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-10 bg-white border-t border-slate-200">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col gap-6">
                    <div className="flex flex-col sm:flex-row gap-4 sm:items-end sm:justify-between">
                        <div className="flex flex-col gap-2">
                            <Logo />
                            <span className="text-xs text-slate-500">QUOTESFLOW AI ROOFING LLC — Dallas County, Texas</span>
                            <span className="text-xs text-slate-500">© {new Date().getFullYear()} QUOTESFLOW AI ROOFING LLC. All rights reserved.</span>
                        </div>
                        <div className="flex flex-col items-start sm:items-end gap-1 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="text-xs uppercase tracking-wider text-slate-500">Support</span>
                                <a
                                    href="mailto:support@quotesflowairoofing.com"
                                    className="text-slate-700 hover:text-slate-900 underline-offset-2 hover:underline"
                                    data-testid="footer-support-email"
                                >
                                    support@quotesflowairoofing.com
                                </a>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs uppercase tracking-wider text-slate-500">Legal</span>
                                <a
                                    href="mailto:legal@quotesflowairoofing.com"
                                    className="text-slate-700 hover:text-slate-900 underline-offset-2 hover:underline"
                                    data-testid="footer-legal-email"
                                >
                                    legal@quotesflowairoofing.com
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-slate-100 pt-4">
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Legal</div>
                        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
                            <Link to="/terms" className="hover:text-slate-900" data-testid="footer-terms-link">Terms of Service</Link>
                            <Link to="/privacy" className="hover:text-slate-900" data-testid="footer-privacy-link">Privacy Policy</Link>
                            <Link to="/refund-policy" className="hover:text-slate-900" data-testid="footer-refund-link">Refund Policy</Link>
                            <Link to="/acceptable-use" className="hover:text-slate-900" data-testid="footer-aup-link">Acceptable Use</Link>
                            <Link to="/contractor-addendum" className="hover:text-slate-900" data-testid="footer-contractor-link">Contractor Addendum</Link>
                            <Link to="/disclosures" className="hover:text-slate-900" data-testid="footer-disclosures-link">Disclosures</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
